//import labelmake from "labelmake";
import { el } from 'date-fns/locale';

import { generatePdfUint8Array } from '../pdfUtilities';
import { Font, Template } from '@pdfme/common';
import { receiptData } from '../../types/receiptData';

export const receiptBasePdf = async (
  receipt: receiptData,
  font: Font,
  basePdf: string
) => {
  const firstData = receipt;

  const receiptUnit = '式';

  let estimatesSchema: any = {};
  let estimatesInputs: any = {};

  // 会社ロゴの画像のサイズを指定
  const companySymbolUrl =
    receipt.company_symbol && receipt.company_symbol !== ''
      ? `data:${receipt.company_symbol_format};base64,${receipt.company_symbol}`
      : '';
  const symbolMaxWidth = 30;
  const symbolMaxHeight = 30;
  const companySymbolSize = await getImageSize(
    companySymbolUrl,
    symbolMaxWidth,
    symbolMaxHeight
  );

  // 会社印の画像のサイズを指定
  const companyStampUrl =
    receipt.company_stamp && receipt.company_stamp !== ''
      ? `data:${receipt.company_stamp_format};base64,${receipt.company_stamp}`
      : '';
  const stampMaxWidth = 25;
  const stampMaxHeight = 25;
  const companyStampSize = await getImageSize(
    companyStampUrl,
    stampMaxWidth,
    stampMaxHeight
  );

  const template: Template = {
    basePdf: basePdf,
    schemas: [
      {
        client_name: {
          // 顧客
          type: 'text',
          position: {
            x: 20,
            y: 45
          },
          width: 100,
          height: 7,
          alignment: 'left',
          fontSize: 18,
          characterSpacing: 0,
          lineHeight: 1
        },
        receipt_code: {
          // 領収書ナンバー
          type: 'text',
          position: {
            x: 240,
            y: 12
          },
          width: 35,
          height: 7,
          alignment: 'center',
          fontSize: 18,
          characterSpacing: 0,
          lineHeight: 1
        },
        // CS確認で不必要に。発行回数の需要があれば再度表示。
        // published_number: { // 領収書発行回数
        //   type: "text",
        //   position: {
        //     x: 165,
        //     y: 16
        //   },
        //   width: 35,
        //   height: 7,
        //   alignment: "center",
        //   fontSize: 18,
        //   characterSpacing: 0,
        //   lineHeight: 1
        // },
        tax_free_billing_for_ten_percent: {
          // 10%の部分に表示される税抜価格
          type: 'text',
          position: {
            x: 76,
            y: 130.4
          },
          width: 40,
          height: 10,
          alignment: 'left',
          dynamicFontSize: {
            max: 18,
            min: 6
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        ten_percent_tax: {
          // 10%の消費税額
          type: 'text',
          position: {
            x: 76,
            y: 140.4
          },
          width: 40,
          height: 10,
          alignment: 'left',
          dynamicFontSize: {
            max: 18,
            min: 6
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        tax_free_billing_for_eight_percent: {
          // 8%の部分に表示される税抜価格
          type: 'text',
          position: {
            x: 76,
            y: 151.8
          },
          width: 40,
          height: 10,
          alignment: 'left',
          dynamicFontSize: {
            max: 18,
            min: 6
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        eight_percent_tax: {
          // 8%の消費税額
          type: 'text',
          position: {
            x: 76,
            y: 161.8
          },
          width: 40,
          height: 10,
          alignment: 'left',
          dynamicFontSize: {
            max: 18,
            min: 6
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        contract_name: {
          // 工事名称
          type: 'text',
          position: {
            x: 100,
            y: 91.3
          },
          width: 135,
          height: 7,
          alignment: 'left',
          dynamicFontSize: {
            max: 18,
            min: 12
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        confirm_date_str: {
          // 入金確認日
          type: 'text',
          position: {
            x: 80,
            y: 109
          },
          width: 60,
          height: 7,
          alignment: 'left',
          fontSize: 18,
          characterSpacing: 0,
          lineHeight: 1
        },
        billing_total_including_tax: {
          // 今回請求の合計金額
          fontName: 'minchoBold',
          type: 'text',
          position: {
            x: 104,
            y: 67
          },
          width: 95,
          height: 24,
          alignment: 'left',
          dynamicFontSize: {
            max: 22,
            min: 18
          },
          characterSpacing: 0,
          lineHeight: 2.5
        },
        company_symbol: {
          // 会社ロゴ
          type: 'image',
          position: {
            x: 128,
            y: 138
          },
          width: companySymbolSize.width,
          height: companySymbolSize.height,
          alignment: 'left',
          fit: 'contain',
          fontSize: 13,
          characterSpacing: 0,
          lineHeight: 1
        },
        company_name: {
          // 自社名
          type: 'text',
          position: {
            x: 170,
            y: 133
          },
          width: 110,
          height: 7,
          alignment: 'left',
          dynamicFontSize: {
            max: 20,
            min: 16
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        company_code: {
          // 登録番号
          type: 'text',
          position: {
            x: 170,
            y: 145
          },
          width: 110,
          height: 7,
          alignment: 'left',
          fontSize: 16,
          characterSpacing: 0,
          lineHeight: 1
        },
        company_postal_code: {
          // 郵便番号
          type: 'text',
          position: {
            x: 170,
            y: 151
          },
          width: 110,
          height: 7,
          alignment: 'left',
          fontSize: 16,
          characterSpacing: 0,
          lineHeight: 1
        },
        company_address: {
          // 住所
          type: 'text',
          position: {
            x: 170,
            y: 157
          },
          width: 110,
          height: 7,
          alignment: 'left',
          dynamicFontSize: {
            max: 20,
            min: 12
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        company_phone_number: {
          // 電話番号
          type: 'text',
          position: {
            x: 170,
            y: 171
          },
          width: 100,
          height: 7,
          alignment: 'left',
          fontSize: 16,
          characterSpacing: 0,
          lineHeight: 1
        },
        company_fax_number: {
          // FAX番号
          type: 'text',
          position: {
            x: 170,
            y: 177
          },
          width: 100,
          height: 7,
          alignment: 'left',
          fontSize: 16,
          characterSpacing: 0,
          lineHeight: 1
        },
        companyStamp: {
          // 会社印
          type: 'image',
          position: {
            x: 240,
            y: 172
          },
          width: companyStampSize.width,
          height: companyStampSize.height,
          alignment: 'left',
          fontSize: 16,
          characterSpacing: 0,
          lineHeight: 1
        },

        ...estimatesSchema
      }
    ]
  };

  const itemInputs: any = {};
  // 領収書番号
  itemInputs[`receipt_code`] = `No.  ${receipt.code}`;
  // CS確認で不必要に。発行回数の需要があれば再度表示。
  // 領収書発行回数
  // if (receipt.published_number===1) {
  //   itemInputs[`published_number`] = "";
  // } else {
  //   itemInputs[`published_number`] = `発行回数 ${receipt.published_number} 回`;
  // };
  // 税額表示
  if (receipt.tax_rate === 0.1 || receipt.tax_rate === 0.08) {
    itemInputs[`tax_free_billing_for_ten_percent`] = `¥ ${Number(
      receipt.tax_free_billing_for_ten_percent !== null &&
        receipt.tax_free_billing_for_ten_percent > 0
        ? receipt.tax_free_billing_for_ten_percent
        : 0
    ).toLocaleString('ja-JP')}-`; // 税抜価格を表示(10%の部分に表示)
    itemInputs[`tax_free_billing_for_eight_percent`] = `¥ ${Number(
      receipt.tax_free_billing_for_eight_percent > 0
        ? receipt.tax_free_billing_for_eight_percent
        : 0
    ).toLocaleString('ja-JP')}-`; // 税抜価格を表示(8%の部分に表示)
    itemInputs[`eight_percent_tax`] = `¥ ${Number(
      receipt.eight_percent_tax > 0 ? receipt.eight_percent_tax : 0
    ).toLocaleString('ja-JP')}-`; // 税抜価格を表示(8%の部分に表示)
    itemInputs[`ten_percent_tax`] = `¥ ${Number(
      receipt.ten_percent_tax > 0 ? receipt.ten_percent_tax : 0
    ).toLocaleString('ja-JP')}-`; // 税抜価格を表示(10%の部分に表示)
  } else {
    itemInputs[`tax_free_billing_for_ten_percent`] = ''; // 税抜価格を10%の部分に表示
    itemInputs[`tax_free_billing_for_eight_percent`] = ''; // 税抜価格を8%の部分に表示
    itemInputs[`ten_percent_tax`] = ''; // 税額を10%の部分に表示
    itemInputs[`eight_percent_tax`] = ''; // 税額を8%の部分に表示
  }

  itemInputs[`billing_total_including_tax`] = `¥ ${Number(
    receipt.ten_percent_billing || 0
  ).toLocaleString('ja-JP')}-`; // 今回請求の合計金額
  itemInputs[`contract_name`] = `${receipt.construct_name || ''} の代金として`; // 工事名称
  itemInputs[`confirm_date_str`] = receipt.confirm_date_str || ''; // 入金確認日
  itemInputs[`company_name`] = receipt.company_name || ''; // 会社名
  itemInputs[`company_code`] = receipt.company_code
    ? `登録番号：${receipt.company_code}`
    : ''; // 登録番号
  itemInputs[`company_postal_code`] = receipt.company_postal_code
    ? `〒${receipt.company_postal_code.slice(
        0,
        3
      )} - ${receipt.company_postal_code.slice(-4)}`
    : '';
  itemInputs[`company_address`] = receipt.company_address || ''; // 住所
  itemInputs[`company_phone_number`] = receipt.company_phone_number
    ? `TEL：${receipt.company_phone_number}`
    : ''; // 電話番号
  itemInputs[`company_fax_number`] = receipt.company_fax_number
    ? `FAX：${receipt.company_fax_number}`
    : ''; // ファックス番号
  itemInputs[`company_symbol`] =
    receipt.company_symbol && receipt.company_symbol !== ''
      ? `data:${receipt.company_symbol_format};base64,${receipt.company_symbol}`
      : ''; // 会社ロゴ
  itemInputs[`client_name`] = `${receipt.client_name || ''} ${
    receipt.honorific_title || ''
  }`; // 請求先顧客と敬称
  itemInputs[`companyStamp`] =
    receipt.company_stamp && receipt.company_stamp !== ''
      ? `data:${receipt.company_stamp_format};base64,${receipt.company_stamp}`
      : ''; // 印鑑

  const inputs = [{ ...itemInputs, ...estimatesInputs }];
  return await generatePdfUint8Array(template, inputs, font);
};

//指定されたURLから画像を読み込み、最大幅と最大高さを考慮して調整された画像サイズを返す関数
const getImageSize = (
  url: string,
  maxWidth: number,
  maxHeight: number
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    if (!url) {
      // 画像URLが存在しない場合、デフォルトサイズを返す
      resolve({ width: 0, height: 0 });
      return;
    }

    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      let width, height;

      if (img.width > img.height) {
        width = Math.min(maxWidth, maxHeight * aspectRatio);
        height = width / aspectRatio;
      } else {
        height = Math.min(maxHeight, maxWidth / aspectRatio);
        width = height * aspectRatio;
      }

      resolve({ width, height });
    };
    img.onerror = () => {
      // エラーが発生した場合、デフォルトサイズを返す
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
};
